import { type FC } from 'react';
import { type Control, type FieldErrors, get, useWatch } from 'react-hook-form';

import { _notNil } from '@/littledash';

import type { TreatmentMethod } from '@/model/Treatment.model';

import ControlledInputWithUnits from '@/components/UI/FormElements/ControlledInputWithUnits';

import type { TreatmentFormField, TreatmentFormUnit } from './TreatmentForm.model';

const REQUIRED_AT_DOSING_TOOLTIP = 'When marked as required a value must be entered at dosing';
const REQUIRED_AT_SETUP_TOOLTIP =
  'When marked as required a value must be entered here because dose calculations are locked';

export interface DosingMethodFieldProps {
  id: string;
  /** FieldValues is intentional — field names are built dynamically from dosing method keys */
  control: Control;
  dosingType: TreatmentMethod;
  field: TreatmentFormField;
  errors: FieldErrors;
  isSubmitting: boolean;
  units: Array<TreatmentFormUnit>;
  unitsDisabled?: boolean;
  allowZero?: boolean;
  lockDosingInputs?: boolean;
}

const DosingMethodField: FC<DosingMethodFieldProps> = ({
  id,
  control,
  dosingType,
  field,
  errors,
  isSubmitting,
  units,
  unitsDisabled,
  allowZero = false,
  lockDosingInputs = false,
}) => {
  const requiredCheckboxChecked = useWatch({ control, name: `${id}.required` }) === true;
  const requiredAtDosing = dosingType === 'custom' && requiredCheckboxChecked;
  // A locked Custom field can no longer be filled at dosing, so its value is required here instead.
  const requireValueAtSetup = field.required === true || (lockDosingInputs && requiredAtDosing);
  const requiredTooltip = lockDosingInputs ? REQUIRED_AT_SETUP_TOOLTIP : REQUIRED_AT_DOSING_TOOLTIP;

  return (
    <div
      className="w-50"
      data-test-component="DosingMethodField"
      data-test-element="container"
      data-test-key={field.name}
    >
      <label>
        {field.label} {requireValueAtSetup && '(required)'}
      </label>
      <fieldset className="w-100 relative flex justify-between">
        <ControlledInputWithUnits
          id={id}
          classes="w-100"
          field={field}
          selectRequired={dosingType === 'custom'}
          disabled={isSubmitting}
          hasError={_notNil(get(errors, id))}
          units={units}
          control={control}
          errors={errors}
          unitsDisabled={unitsDisabled}
          tooltip={id === 'custom.volume' ? requiredTooltip : undefined}
          allowZero={allowZero}
          requireValueAtSetup={requireValueAtSetup}
        />
      </fieldset>
    </div>
  );
};

export default DosingMethodField;
